// "Ask about your numbers": a search bar above the Control Centre cards.
//
// A question goes to the report-explorer function, every report it runs
// streams back as a card, and a short answer follows. Everything lives in
// this component's state, so leaving the dashboard forgets the conversation.

import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { ArrowUp, Square, X } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import NexusFace from '../NexusFace';
import ReportDeck, { type ReportCardSource } from '../agents/ReportCard';
import { askReportExplorer, type ExplorerTurn } from '../../lib/reportExplorer';
import ResponseRating from '../agents/ResponseRating';

const SUGGESTIONS = [
  'Sales this month vs last month',
  'Which services sold the most this month?',
  'How much cash came in this week?',
  'Missed calls this month',
  'Who is still owing us money?',
];

interface Exchange {
  id: string;
  question: string;
  cards: ReportCardSource[];
  answer: string;
  status: string;
  error: string | null;
  running: boolean;
}

export default function ControlCentreReportSearch() {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const [draft, setDraft] = useState('');
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  const running = exchanges.some((x) => x.running);
  const open = exchanges.length > 0;

  // A different business means different numbers; start over.
  useEffect(() => {
    abortRef.current?.abort();
    setExchanges([]);
    setDraft('');
  }, [businessId]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const patch = (id: string, change: Partial<Exchange> | ((x: Exchange) => Partial<Exchange>)) => {
    setExchanges((list) => list.map((x) => (x.id === id ? { ...x, ...(typeof change === 'function' ? change(x) : change) } : x)));
  };

  const ask = async (raw: string) => {
    const question = raw.trim();
    if (!question || !businessId || running) return;
    const id = crypto.randomUUID();
    const turns: ExplorerTurn[] = exchanges
      .filter((x) => x.answer && !x.error)
      .map((x) => ({ question: x.question, answer: x.answer, formulas: x.cards.map((c) => c.content) }));
    setExchanges((list) => [...list, { id, question, cards: [], answer: '', status: 'Reading the question', error: null, running: true }]);
    setDraft('');
    const abort = new AbortController();
    abortRef.current = abort;
    try {
      await askReportExplorer({ businessId, question, turns }, (event) => {
        if (event.kind === 'status') patch(id, { status: event.text });
        else if (event.kind === 'report') patch(id, (x) => ({ cards: [...x.cards, event.card] }));
        else if (event.kind === 'answer') patch(id, { answer: event.text, status: '' });
        else if (event.kind === 'error') patch(id, { error: event.message, status: '' });
      }, abort.signal);
      patch(id, { running: false, status: '' });
    } catch (err) {
      if (abort.signal.aborted) {
        patch(id, (x) => ({ running: false, status: '', answer: x.answer || (x.cards.length ? 'Stopped. The cards above are what ran.' : ''), error: x.cards.length ? null : 'Stopped.' }));
      } else {
        patch(id, { running: false, status: '', error: err instanceof Error ? err.message : 'The report question failed' });
      }
    } finally {
      if (abortRef.current === abort) abortRef.current = null;
    }
  };

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [exchanges.length, open]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void ask(draft);
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void ask(draft);
    } else if (e.key === 'Escape' && !running && open) {
      clear();
    }
  };

  const stop = () => abortRef.current?.abort();

  const clear = () => {
    abortRef.current?.abort();
    setExchanges([]);
    setDraft('');
    inputRef.current?.focus();
  };

  if (!businessId) return null;

  return (
    <section
      data-control-centre-panel
      aria-label="Ask about your numbers"
      className="mb-3"
    >
      <form onSubmit={submit} className="flex items-center gap-2.5 px-3 py-2 sm:px-4 sm:py-2.5">
        <NexusFace size={30} pulse={running} title="Nexus" />
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder={open ? 'Ask a follow-up…' : 'Ask Nexus about your numbers — sales, cash, leads, calls, anything in your reports'}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="send"
          className="min-w-0 flex-1 bg-transparent text-[15px] text-gray-900 placeholder:text-gray-400 focus:outline-none"
          aria-label="Ask about your numbers"
        />
        {running ? (
          <button
            type="button"
            onClick={stop}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white transition-colors hover:bg-gray-700"
            aria-label="Stop"
          >
            <Square className="h-3 w-3 fill-current" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={!draft.trim()}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white transition-colors hover:bg-gray-700 disabled:bg-gray-200 disabled:text-gray-400"
            aria-label="Ask"
          >
            <ArrowUp className="h-4 w-4" />
          </button>
        )}
        {open && !running ? (
          <button
            type="button"
            onClick={clear}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            aria-label="Clear"
            title="Clear"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </form>

      {!open ? (
        <div className="flex gap-1.5 overflow-x-auto px-3 pb-2.5 sm:px-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {SUGGESTIONS.map((text) => (
            <button
              key={text}
              type="button"
              onClick={() => void ask(text)}
              className="shrink-0 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[12px] text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-900"
            >
              {text}
            </button>
          ))}
        </div>
      ) : null}

      {open ? (
        <div className="control-centre-panel__body !min-h-0 space-y-4 border-t border-gray-200/70">
          {exchanges.map((x) => (
            <article key={x.id} className="space-y-3">
              <p className="text-[13px] font-medium text-gray-500">
                <span className="text-gray-400">You asked · </span>{x.question}
              </p>
              {x.cards.length > 0 ? <ReportDeck cards={x.cards} theme="light" wide /> : null}
              {x.running && x.status ? (
                <p className="flex items-center gap-2 text-[13px] text-gray-500">
                  <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-violet-500" />
                  <span className="truncate font-mono text-[12px]">{x.status}</span>
                </p>
              ) : null}
              {x.answer ? (
                <div>
                  <div className="flex items-start gap-2.5">
                    <NexusFace size={22} className="mt-0.5 shrink-0" />
                    <p className="text-[14px] leading-relaxed text-gray-800">{x.answer}</p>
                  </div>
                  {!x.running && businessId && (
                    <div className="pl-8">
                      <ResponseRating
                        businessId={businessId}
                        surface="control_centre"
                        targetId={x.id}
                        excerpt={`You asked: ${x.question}\n\n${x.answer}`}
                        tone="light"
                      />
                    </div>
                  )}
                </div>
              ) : null}
              {x.error ? (
                <p className="text-[13px] text-red-600">{x.error}</p>
              ) : null}
            </article>
          ))}
          <div ref={endRef} />
        </div>
      ) : null}
    </section>
  );
}
